/**
 * Le o arquivo como um buffer array
 * @param {Arquio que vai ser lido} inputFile
 */
const ReadFileAsArrayBuffer = (inputFile) => {
	const fileReader = new FileReader()

	return new Promise((resolve, reject) => {
		fileReader.onerror = () => {
			fileReader.abort()
			reject(new DOMException('Problem parsing input file.'))
		}

		fileReader.onload = (e) => {
			resolve(e.target.result)
		}
		fileReader.readAsArrayBuffer(inputFile)
	})
}

/** O arquivo é texto, mas em um encoding que não dá para decodificar. */
const MOTIVO_ENCODING_NAO_SUPORTADO = 'ENCODING_NAO_SUPORTADO'
/** O arquivo é uma planilha ou documento renomeado, não um CSV. */
const MOTIVO_ARQUIVO_NAO_EH_CSV = 'ARQUIVO_NAO_EH_CSV'
/** O arquivo é binário, sem formato reconhecido. */
const MOTIVO_ARQUIVO_NAO_EH_TEXTO = 'ARQUIVO_NAO_EH_TEXTO'

/**
 * Falha de leitura em que o arquivo existe mas não dá para extrair texto dele.
 * Serve para a tela diferenciar "arquivo ilegível" de "CSV mal formatado" e
 * dizer ao usuário o que corrigir.
 *
 * A tela discrimina pelo campo `motivo`, não por `instanceof`: o projeto passa
 * por Babel e o alvo do build pode transpilar a classe, o que quebraria a
 * checagem de tipo silenciosamente em produção.
 */
class ArquivoIlegivelError extends Error {
	constructor(motivo, detalhe = '') {
		super(`${motivo}${detalhe ? `: ${detalhe}` : ''}`)
		this.name = 'ArquivoIlegivelError'
		this.motivo = motivo
		this.detalhe = detalhe
	}
}

/** Quantos bytes do início do arquivo são inspecionados na detecção. */
const BYTES_AMOSTRA_DETECCAO = 4096

/**
 * Assinaturas de formatos que chegam renomeados para .csv — o filtro do
 * FileUpload é só por extensão, então nada impede o usuário de renomear a
 * planilha em vez de exportá-la.
 */
const ASSINATURAS_NAO_CSV = [
	[0x50, 0x4b, 0x03, 0x04], // ZIP: .xlsx, .ods, .docx
	[0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1], // OLE2: .xls e .doc antigos
	[0x25, 0x50, 0x44, 0x46], // %PDF
]

const comecaCom = (bytes, assinatura) => assinatura.every((byte, i) => bytes[i] === byte)

/**
 * Distingue UTF-16 sem BOM de um binário qualquer. Em UTF-16 de texto latino
 * metade dos bytes é 0x00 e sempre na mesma paridade de posição (byte alto de
 * cada par), padrão que binário não costuma ter.
 *
 * @param {Uint8Array} amostra
 * @returns {string|null} 'UTF-16 LE', 'UTF-16 BE' ou null
 */
const detectarUtf16SemBom = (amostra) => {
	let nulosEmPosicaoPar = 0
	let nulosEmPosicaoImpar = 0

	for (let i = 0; i < amostra.length; i++) {
		if (amostra[i] !== 0x00) continue

		if (i % 2 === 0) nulosEmPosicaoPar++
		else nulosEmPosicaoImpar++
	}

	// 80% dos pares, para tolerar algum caractere fora do plano latino
	const minimoEsperado = Math.floor(amostra.length / 2) * 0.8

	if (nulosEmPosicaoImpar >= minimoEsperado && nulosEmPosicaoPar === 0) return 'UTF-16 LE'
	if (nulosEmPosicaoPar >= minimoEsperado && nulosEmPosicaoImpar === 0) return 'UTF-16 BE'

	return null
}

/**
 * Identifica arquivos que o ReadFileAsText não consegue decodificar. Só reporta
 * o que é certeza pelos bytes — nada de heurística de idioma.
 *
 * @param {Uint8Array} bytes conteúdo do arquivo
 * @returns {ArquivoIlegivelError|null} erro a lançar, ou null se é decodificável
 */
const detectarArquivoIlegivel = (bytes) => {
	if (ASSINATURAS_NAO_CSV.some((assinatura) => comecaCom(bytes, assinatura))) {
		return new ArquivoIlegivelError(MOTIVO_ARQUIVO_NAO_EH_CSV)
	}

	// BOM de UTF-16/UTF-32. Sem esse teste os decoders de UTF-8 e windows-1252
	// engolem os bytes nulos como texto e o CSV sai ilegível em vez de falhar.
	const encodingPorBom = detectarEncodingPorBom(bytes)

	if (encodingPorBom) {
		return new ArquivoIlegivelError(MOTIVO_ENCODING_NAO_SUPORTADO, encodingPorBom)
	}

	// Sem BOM o sinal é o byte nulo: CSV em UTF-8 ou windows-1252 nunca tem 0x00.
	const amostra = bytes.subarray(0, BYTES_AMOSTRA_DETECCAO)

	if (!amostra.includes(0x00)) return null

	const utf16SemBom = detectarUtf16SemBom(amostra)

	return utf16SemBom
		? new ArquivoIlegivelError(MOTIVO_ENCODING_NAO_SUPORTADO, utf16SemBom)
		: new ArquivoIlegivelError(MOTIVO_ARQUIVO_NAO_EH_TEXTO)
}

/**
 * @param {Uint8Array} bytes
 * @returns {string|null} rótulo do encoding, ou null se não há BOM de UTF-16/32
 */
const detectarEncodingPorBom = (bytes) => {
	if (bytes[0] === 0x00 && bytes[1] === 0x00 && bytes[2] === 0xfe && bytes[3] === 0xff) {
		return 'UTF-32 BE'
	}
	if (bytes[0] === 0xff && bytes[1] === 0xfe) {
		return bytes[2] === 0x00 && bytes[3] === 0x00 ? 'UTF-32 LE' : 'UTF-16 LE'
	}
	if (bytes[0] === 0xfe && bytes[1] === 0xff) {
		return 'UTF-16 BE'
	}

	return null
}

/**
 * Lê o arquivo como texto detectando o encoding: tenta UTF-8 (com/sem BOM) em
 * modo estrito — o decode falha se os bytes não forem UTF-8 válidos — e cai
 * para windows-1252, que é o que o Excel pt-BR gera ao salvar "CSV separado
 * por vírgulas".
 *
 * Ler um arquivo windows-1252 como UTF-8 troca cada acento pelo caractere de
 * substituição (U+FFFD): "Talhão 1" chegava como "Talh<U+FFFD>o 1" e não batia
 * mais com o código cadastrado no sistema.
 *
 * O label é 'windows-1252' e não 'iso-8859-1' de propósito: no browser são
 * apelidos do mesmo decoder, mas em Node/ICU 'iso-8859-1' é Latin-1 puro, e os
 * dois divergem em 0x80–0x9F (aspas curvas que o Word e o Excel inserem).
 *
 * @param {File|Blob} inputFile arquivo que vai ser lido
 * @throws {ArquivoIlegivelError} se o arquivo não for um CSV de texto legível
 * @returns {Promise<string>} conteúdo do arquivo em texto
 */
const ReadFileAsText = async (inputFile) => {
	const buffer = await ReadFileAsArrayBuffer(inputFile)
	const bytes = new Uint8Array(buffer)

	const arquivoIlegivel = detectarArquivoIlegivel(bytes)

	if (arquivoIlegivel) throw arquivoIlegivel

	try {
		return new TextDecoder('utf-8', { fatal: true }).decode(bytes)
	} catch {
		return new TextDecoder('windows-1252').decode(bytes)
	}
}

export {
	ArquivoIlegivelError,
	detectarArquivoIlegivel,
	MOTIVO_ARQUIVO_NAO_EH_CSV,
	MOTIVO_ARQUIVO_NAO_EH_TEXTO,
	MOTIVO_ENCODING_NAO_SUPORTADO,
	ReadFileAsArrayBuffer,
	ReadFileAsText,
}
